import React, { useEffect, useRef, useState } from 'react';
import { heroStore, useHero } from '../lib/heroStore.js';

const kb = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)} MB` : n >= 1e4 ? `${(n / 1e3).toFixed(1)} kB` : `${n.toLocaleString('en-US')} B`);

async function gzipSize(bytes) {
  if (typeof CompressionStream === 'undefined') return null;
  const s = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
  return (await new Response(s).arrayBuffer()).byteLength;
}

// Measured by pulse-player (docs/binary.md), Node's zlib: minified JSON, gzip -9, .pulse.
const MEASURED = [
  ['Kernel Space showcase (210 s, 830 instances, 27 259 vertices)', 1000258, 114287, 48770],
  ['5 000 shapes, compact paths (unit-test document)', 1574985, 417095, 206086],
  ['chatbot speech card', 29918, 4639, 1605],
  ['lyric video', 15706, 3177, 2687],
];

export default function BinarySection() {
  const stress = useHero((s) => s.stress);
  const hasDoc = useHero((s) => !!s.doc);
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const seen = useRef(false);

  const run = async () => {
    const { Pulse, doc } = heroStore.get();
    if (!Pulse || !doc) return;
    setBusy(true);
    try {
      const json = new TextEncoder().encode(JSON.stringify(doc));
      const gz = await gzipSize(json);
      let t = performance.now();
      const bin = await Pulse.binary.encode(doc);
      const encMs = performance.now() - t;
      t = performance.now();
      const back = await Pulse.binary.decode(bin);
      const decMs = performance.now() - t;
      const same = JSON.stringify(back.doc) === JSON.stringify(doc);
      setR({ json: json.length, gz, bin: bin.length, encMs, decMs, same, head: Array.from(bin.slice(0, 24)), stress: heroStore.get().stress });
    } finally { setBusy(false); }
  };

  // Encode once when the section first scrolls into view.
  useEffect(() => {
    if (!hasDoc || seen.current || !box.current) return undefined;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting && !seen.current) { seen.current = true; run(); } }, { rootMargin: '100px' });
    io.observe(box.current);
    return () => io.disconnect();
  }, [hasDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  const max = r ? r.json : 1;
  const bars = r ? [
    ['JSON', r.json, 'json'],
    ...(r.gz != null ? [['JSON + gzip', r.gz, 'gz']] : []),
    ['.pulse', r.bin, 'bin'],
  ] : [];

  return (
    <section className="sec" id="binary" aria-labelledby="binary-h" ref={box}>
      <div className="wrap">
        <header className="sec-head">
          <p className="kicker">The .pulse format</p>
          <h2 id="binary-h">The hero scene, packed in your browser.</h2>
          <p className="sec-lead">
            A <code>.pulse</code> file holds one scene and, optionally, its assets in a compact, checksummed
            container: a shared string table with a built-in dictionary, object shapes stored once, keyframe
            and vertex lists stored by column, delta-coded number arrays, then deflate. Lossless by default.
            This encodes the hero document exactly as it is playing now.
          </p>
        </header>
        <div className="bin-grid">
          <div className="panel">
            <div className="panel-head">
              <h3>Hero document at stress ×{r ? r.stress : stress}</h3>
              <button type="button" className="wx-btn wx-btn--sm wx-btn--pill" onClick={run} disabled={busy || !hasDoc}>
                {busy ? 'Encoding…' : r && r.stress !== stress ? `Re-encode at ×${stress}` : 'Encode again'}
              </button>
            </div>
            {!r && <p className="dim">{hasDoc ? 'Encoding…' : 'Waiting for the hero scene.'}</p>}
            {r && (
              <>
                <div className="bars" role="list">
                  {bars.map(([label, v, cls]) => (
                    <div className="bar-row" role="listitem" key={label}>
                      <span className="bar-label">{label}</span>
                      <span className="bar-track"><span className={`bar-fill ${cls}`} style={{ width: `${Math.max(0.6, v / max * 100)}%` }} /></span>
                      <span className="bar-val">{kb(v)}</span>
                    </div>
                  ))}
                </div>
                <p className="bin-facts">
                  <span><b>{(r.json / r.bin).toFixed(0)}×</b> smaller than JSON</span>
                  {r.gz != null && <span><b>{Math.round(r.bin / r.gz * 100)}%</b> of the gzipped JSON</span>}
                  <span>encoded in <b>{r.encMs.toFixed(0)} ms</b>, decoded in <b>{r.decMs.toFixed(0)} ms</b></span>
                  <span>{r.same ? 'decodes to an identical document' : 'round trip differs'}</span>
                </p>
                <div className="hex" aria-label="The first bytes of the file">
                  {r.head.map((b, i) => (
                    <span key={i} className={i < 4 ? 'hx-magic' : i < 8 ? 'hx-head' : ''}>{b.toString(16).padStart(2, '0')}</span>
                  ))}
                  <span className="hex-note">“PULS”, version, flags, section count, length, then the section table</span>
                </div>
              </>
            )}
          </div>
          <div className="panel">
            <h3>Measured on real documents</h3>
            <table className="wx-table tbl">
              <thead><tr><th>document</th><th>JSON</th><th>gzip -9</th><th>.pulse</th></tr></thead>
              <tbody>
                {MEASURED.map(([name, j, g, p]) => (
                  <tr key={name}><td>{name}</td><td>{kb(j)}</td><td>{kb(g)}</td><td><b>{kb(p)}</b></td></tr>
                ))}
              </tbody>
            </table>
            <p className="dim small">From pulse-player <code>docs/binary.md</code>. Every example document packs smaller than its gzipped JSON; the unit tests check it.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
